import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { QueuedAttendance } from './queue';
import type { QueuedCanje } from './canjeQueue';
import type { QueuedVisita } from './visitaQueue';
import type { QueuedJornada } from './jornadaQueue';
import type { QueuedVisitaSoftys } from './softysQueue';

export const DB_NAME = 'treid-campo';
/**
 * v2 (2026-08-12): se agrega el store de canjes pendientes.
 * v3 (2026-08-18): se agrega el de visitas del levantamiento de canal.
 * v4 (2026-08-18): y el del cierre de jornada, que también se llena dentro del mercado.
 * v5 (2026-09-14): el de las visitas de Softys. Va store aparte y NO una generalización, por el
 *   mismo motivo que las otras: la campaña recorre mercados en Iquitos, Juliaca, Tacna y
 *   Andahuaylas, donde la señal falla, y el dato que guarda —dos formas distintas, con foto de
 *   fachada en cada una— no se parece al de ninguna cola existente.
 *
 * El `upgrade` es acumulativo y cada store se crea solo si falta, así que un celular que venía
 * en v1 o v2 **conserva sus pendientes** y solo gana el store nuevo. Perder la cola en una
 * actualización sería perder asistencia ya marcada sin señal.
 */
export const DB_VERSION = 5;
export const STORE_PENDING = 'pending_attendance' as const;
export const STORE_PENDING_CANJES = 'pending_canjes' as const;
export const STORE_PENDING_VISITAS = 'pending_visitas' as const;
export const STORE_PENDING_JORNADAS = 'pending_jornadas' as const;
export const STORE_PENDING_SOFTYS = 'pending_softys' as const;

/** Los stores guardan el dato más su historial de reintentos. No hay índices sobre esos campos,
 *  así que agregarlos NO requiere subir `DB_VERSION`: los registros viejos se leen con los
 *  defaults que aplica cada cola.
 *
 *  POR ESO EL CAMBIO DEL 2026-08-19 NO SUBIÓ LA VERSIÓN. `pending_visitas` pasó a guardar varias
 *  fotos por clave, tres campos nuevos (`status: 'enviada'`, `guardadaEn`, `enviadaEn`) y a
 *  CONSERVAR las fichas ya subidas para poder pintar el avance del día sin señal. Todo eso es
 *  forma del VALOR, no del esquema: no hay índice ni store nuevo que crear, y `visitaQueue`
 *  normaliza lo que encuentre (una ficha guardada con la forma vieja se lee igual). Subir la
 *  versión sin necesidad tiene costo real: dispara el `upgrade`, y si hay otra pestaña abierta
 *  con la versión vieja la apertura se bloquea — ver `APERTURA_MAX_MS` acá abajo. */
export interface TreidCampoDB extends DBSchema {
  pending_attendance: {
    key: string;
    value: QueuedAttendance;
  };
  pending_canjes: {
    key: string;
    value: QueuedCanje;
  };
  pending_visitas: {
    key: string;
    value: QueuedVisita;
  };
  pending_jornadas: {
    key: string;
    value: QueuedJornada;
  };
  pending_softys: {
    key: string;
    value: QueuedVisitaSoftys;
  };
}

let dbPromise: Promise<IDBPDatabase<TreidCampoDB>> | null = null;

/**
 * 🔴 ESTA PESTAÑA CORRE CÓDIGO VIEJO CONTRA UNA BASE NUEVA.
 *
 * IndexedDB no deja abrir con una versión menor a la que ya tiene guardada: tira
 * `VersionError` y el navegador lo dice en inglés —«The requested version (4) is less than
 * the existing version (5)»—. Pasa cuando una pestaña sigue con el bundle anterior después
 * de que otra ya actualizó el esquema.
 *
 * Lo importante NO es el mensaje, es que **reintentar no puede funcionar**: el código que
 * está corriendo va a pedir la misma versión vieja todas las veces. Ofrecer «Reintentar» ahí
 * es mandar a alguien a apretar un botón que nunca va a andar. Lo único que arregla esto es
 * cargar el código nuevo, o sea recargar.
 */
export const ERROR_VERSION_VIEJA = 'version_vieja_de_la_app';

export function esErrorDeVersionVieja(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.name === ERROR_VERSION_VIEJA
    || err.name === 'VersionError'
    || /less than the existing version/i.test(err.message);
}

/**
 * Cuánto se espera a que abra la base local antes de darla por perdida.
 *
 * POR QUÉ EXISTE ESTE TOPE: cuando una versión nueva necesita actualizar el esquema y OTRA
 * pestaña de la app sigue con la versión vieja abierta, el navegador **no falla**: deja la
 * apertura esperando indefinidamente (dispara `blocked` y nada más). `openDB` devuelve
 * entonces una promesa que no resuelve nunca, y cualquier pantalla que la espere se queda
 * cargando para siempre. Sin este tope, subir `DB_VERSION` puede dejar a alguien mirando
 * "Cargando tus turnos…" sin ningún error que explique nada.
 */
const APERTURA_MAX_MS = 3000;

/**
 * Abre (una sola vez por sesión) la base local `treid-campo`.
 *
 * Falla en vez de colgarse: si no abre en `APERTURA_MAX_MS`, rechaza con un mensaje que dice
 * qué hacer, y olvida la promesa para que el próximo intento vuelva a probar. Quien la consuma
 * decide qué hacer con el error — pero nadie se queda esperando.
 */
export function getDb(): Promise<IDBPDatabase<TreidCampoDB>> {
  if (!dbPromise) {
    const apertura = openDB<TreidCampoDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          db.createObjectStore(STORE_PENDING, { keyPath: 'clientUuid' });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING_CANJES)) {
          db.createObjectStore(STORE_PENDING_CANJES, { keyPath: 'clientUuid' });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING_VISITAS)) {
          db.createObjectStore(STORE_PENDING_VISITAS, { keyPath: 'clientUuid' });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING_JORNADAS)) {
          db.createObjectStore(STORE_PENDING_JORNADAS, { keyPath: 'clientUuid' });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING_SOFTYS)) {
          db.createObjectStore(STORE_PENDING_SOFTYS, { keyPath: 'clientUuid' });
        }
      },
      /** Otra pestaña con la versión vieja nos impide actualizar. */
      blocked() {
        console.warn(
          '[treid] La base local no puede actualizarse: hay otra pestaña de la app abierta. '
          + 'Ciérrala y recarga.',
        );
      },
      /** Nosotros tenemos abierta una versión vieja y otra pestaña quiere actualizar: soltamos,
       *  para no ser nosotros los que dejamos colgada a la otra. */
      blocking(_current, _blocked, event) {
        (event.target as IDBDatabase | null)?.close();
        dbPromise = null;
      },
      /** El navegador cerró la conexión por su cuenta (poco frecuente, pero pasa). */
      terminated() {
        dbPromise = null;
      },
    });

    let reloj: ReturnType<typeof setTimeout>;
    const tope = new Promise<never>((_, reject) => {
      reloj = setTimeout(
        () => reject(new Error(
          'La base local no respondió. Si tienes la app abierta en otra pestaña, ciérrala y '
          + 'vuelve a cargar.',
        )),
        APERTURA_MAX_MS,
      );
    });

    dbPromise = Promise.race([apertura, tope])
      .then((db) => { clearTimeout(reloj); return db; })
      .catch((err: unknown) => {
        clearTimeout(reloj);
        // Se olvida para que un reintento posterior (otra pantalla, otro momento) pueda abrir.
        dbPromise = null;
        if (esErrorDeVersionVieja(err)) {
          const propio = new Error(
            'Esta pesta\u00f1a tiene una versi\u00f3n vieja de la app. Recarga para actualizarla.',
          );
          propio.name = ERROR_VERSION_VIEJA;
          throw propio;
        }
        throw err;
      });
  }
  return dbPromise;
}

/**
 * 🔴 LA CONEXIÓN A LA BASE LOCAL SE MUERE SOLA, Y SAFARI ES EL QUE MÁS LO HACE.
 *
 * El 14-set a las 10:49 una impulsadora en el Mercado Polvos Rosados de Tacna quedó trabada con
 * *«Connection to Indexed Database server lost»* y el puesto ya tipeado en pantalla. WebKit cierra
 * la conexión de IndexedDB cuando la página vuelve de segundo plano o hay presión de memoria —y en
 * un mercado la persona cambia de app todo el tiempo: cámara, WhatsApp, cámara—.
 *
 * `getDb()` no falla en ese caso: devuelve la conexión memorizada, que ya está muerta, y **el que
 * revienta es el `get`/`put`**. Por eso el reintento tiene que envolver la OPERACIÓN y no la
 * apertura. El callback `terminated` ya olvida la promesa; acá se fuerza para el caso en que el
 * error llegue antes que el callback.
 */
function esConexionPerdida(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /connection to indexed database server lost/i.test(err.message)
    || /database connection is closing/i.test(err.message)
    || err.name === 'InvalidStateError';
}

/** Corre algo contra la base local; si la conexión se había muerto, la reabre y reintenta UNA vez. */
export async function conBaseLocal<T>(
  fn: (db: IDBPDatabase<TreidCampoDB>) => Promise<T>,
): Promise<T> {
  try {
    return await fn(await getDb());
  } catch (err) {
    if (!esConexionPerdida(err)) throw err;
    dbPromise = null;
    return await fn(await getDb());
  }
}

/** Cierra la conexión y olvida la memorizada (tests y logout). */
export async function closeDb(): Promise<void> {
  if (!dbPromise) return;
  const db = await dbPromise;
  db.close();
  dbPromise = null;
}
